import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../../services/api';
import versionHistoryService from '../../services/versionHistoryService';
import AddUserPermissionModal from './AddUserPermissionModal';
import ScoringTab from './ScoringTab';
import PublishAssessmentModal from './PublishAssessmentModal';
import { useAuth } from '../../contexts/AuthContext';
import { unpublishAssessment } from '../../services/reviewWorkflowApi';
import ConfirmDialog from '../common/ConfirmDialog';
import { ReviewHistoryInline } from './ReviewHistory';
import { buildAssessmentCsv, assessmentCsvFilename, downloadCsv } from '../../utils/assessmentCsvExport';
import {
  X,
  FileText,
  Globe,
  Smartphone,
  Tablet,
  Monitor,
  Plus,
  Edit2,
  Trash2,
  Copy,
  Archive,
  CheckCircle,
  Clock,
  Eye,
  Calendar,
  Tag,
  Settings,
  BookOpen,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Zap,
  Download,
  Upload,
  Users,
  Activity,
  Shield,
  Briefcase,
  MoreHorizontal,
  ExternalLink,
  Layers,
  Calculator,
  Loader2,
  Lock,
  Undo2
} from 'lucide-react';
import { Avatar, ToggleRow, EmptyState } from '../ui';

// Status chip tones, keyed by statusConfig colour (FECOA-2174).
const STATUS_CHIP = {
  amber: 'badge-warning',
  blue: 'badge-info',
  teal: 'badge-teal',
  red: 'badge-danger',
  emerald: 'badge-success',
  gray: 'badge-gray',
};

// Summary number in the original flexCOA style: small uppercase label with a
// brand icon and a bold number (FECOA-2174).
const OverviewStat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <div className="stat-label">
      <Icon className="h-3.5 w-3.5 flex-shrink-0 text-primary-500" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </div>
    <p className="stat-value">{value}</p>
  </div>
);

/**
 * Manage Assessment - Comprehensive assessment management UI
 * Central hub for viewing/editing assessment details, managing variants, etc.
 */
const ManageAssessment = ({ assessment, onClose, onEdit, onView, onCreateVariant, onDuplicate, onDelete, onRefresh, onUpdate, autoFocusRename = false, defaultTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [editingName, setEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [assessmentName, setAssessmentName] = useState(assessment?.name || assessment?.survey?.name || '');
  const [assessmentDescription, setAssessmentDescription] = useState(assessment?.description || '');
  
  // Current assessment data (can be updated when settings change)
  const [currentAssessment, setCurrentAssessment] = useState(assessment);
  const [showPublishModal, setShowPublishModal] = useState(false);
  
  // Usage and users data
  const [usageData, setUsageData] = useState({ studies: [], users: [], changeLog: [] });
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  
  // Overview stats data
  const [overviewStats, setOverviewStats] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  
  // Version history data
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // Variants data
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  
  // Fetch variants on mount and when Variants tab is selected
  useEffect(() => {
    if (assessment?.id) {
      fetchVariants();
    }
  }, [assessment?.id]);
  
  // Auto-focus rename if prop is set (but not for published assessments)
  useEffect(() => {
    if (autoFocusRename && currentAssessment?.publication_status !== 'published') {
      setEditingName(true);
    }
  }, [autoFocusRename, currentAssessment?.publication_status]);
  
  const fetchVariants = async () => {
    setLoadingVariants(true);
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessment.uuid}/variants/`);
      const data = response.data;
      console.log('[ManageAssessment] Variants API response:', data);
      setVariants(data.variants || []);
    } catch (error) {
      console.error('[ManageAssessment] Error fetching variants:', error);
    } finally {
      setLoadingVariants(false);
    }
  };
  
  // Handler for when assessment data is updated (e.g., from settings save)
  const handleAssessmentUpdate = (updatedAssessment) => {
    console.log('[ManageAssessment] handleAssessmentUpdate called with:', updatedAssessment);
    setCurrentAssessment(updatedAssessment);
    // Also notify parent component
    if (onUpdate) {
      onUpdate(updatedAssessment);
    }
  };

  // Status configuration — must cover the full review lifecycle
  // (draft / in_review / approved / published / denied / archived). `review` is
  // kept as a legacy alias for the renamed `in_review` value.
  const statusConfig = {
    draft: { icon: Clock, color: 'amber', label: 'Draft' },
    review: { icon: Eye, color: 'blue', label: 'Under Review' },
    in_review: { icon: Eye, color: 'blue', label: 'Under Review' },
    approved: { icon: CheckCircle, color: 'teal', label: 'Approved' },
    denied: { icon: AlertCircle, color: 'red', label: 'Changes Requested' },
    published: { icon: CheckCircle, color: 'emerald', label: 'Published' },
    archived: { icon: Archive, color: 'gray', label: 'Archived' }
  };

  // Fall back to draft for any unrecognized status so an unknown value can never
  // crash the modal (this is what broke on the new `in_review` state).
  const currentStatus = statusConfig[currentAssessment?.publication_status] || statusConfig.draft;

  // Publish is available once approved, or straight from draft when approval is
  // not enforced for this assessment (enforce_approval resolved server-side).
  const canPublish =
    currentAssessment?.publication_status === 'approved' ||
    (currentAssessment?.publication_status === 'draft' &&
      currentAssessment?.enforce_approval === false);

  // Get device icon
  const getDeviceIcon = (deviceType) => {
    const icons = {
      phone: Smartphone,
      tablet: Tablet,
      desktop: Monitor,
      other: Globe
    };
    return icons[deviceType] || Globe;
  };

  const handleSaveName = async () => {
    if (!assessmentName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    // Don't allow renaming published assessments
    if (currentAssessment?.publication_status === 'published') {
      toast.error('Published assessments cannot be renamed');
      return;
    }
    
    setSavingName(true);
    try {
      const oldName = currentAssessment?.name || currentAssessment?.survey?.name || '';
      const response = await api.patch(`/api/assessment-builder/assessments/${currentAssessment.uuid}/`, { name: assessmentName.trim() });
      const updated = response.data;
      // Preserve id in case API doesn't return it
      setCurrentAssessment(prev => ({ ...prev, ...updated, id: prev.id }));
      toast.success('Name updated successfully');
      setEditingName(false);
      // Create a version snapshot for the name change
      const surveyId = assessment?.survey_id || assessment?.survey?.id;
      if (surveyId) {
        try {
          await versionHistoryService.renameSnapshot(surveyId, {}, oldName, assessmentName.trim());
        } catch (vErr) {
          console.warn('Failed to create rename version snapshot:', vErr);
        }
      }
      // Refresh the assessment list in parent component
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error updating name:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Failed to update name';
      toast.error(errorMsg);
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveDescription = async () => {
    try {
      const response = await api.patch(`/api/assessment-builder/assessments/${currentAssessment.uuid}/`, { description: assessmentDescription });
      const updated = response.data;
      setCurrentAssessment(prev => ({ ...prev, ...updated, id: prev.id }));
      toast.success('Description updated successfully');
      setEditingDescription(false);
      // Create a version snapshot for the description change
      const surveyId = assessment?.survey_id || assessment?.survey?.id;
      if (surveyId) {
        try {
          await versionHistoryService.manualSave(surveyId, {}, 'Description updated');
        } catch (vErr) {
          console.warn('Failed to create description version snapshot:', vErr);
        }
      }
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error updating description:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Failed to update description';
      toast.error(errorMsg);
    }
  };

  // Variant action handlers
  const handleArchiveVariant = async (variant) => {
    if (!window.confirm(`Are you sure you want to archive the variant "${variant.language} - ${variant.device_name}"?`)) {
      return;
    }
    try {
      await api.delete(`/api/assessment-builder/assessments/${variant.variant_assessment_uuid}/`);
      toast.success('Variant archived successfully');
      fetchVariants();
    } catch (error) {
      console.error('Error archiving variant:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to archive variant';
      toast.error(errorMsg);
    }
  };

  const handleDeleteVariant = async (variant) => {
    if (!window.confirm(`Are you sure you want to permanently delete the variant "${variant.language} - ${variant.device_name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/api/assessment-builder/assessments/${variant.variant_assessment_uuid}/`);
      toast.success('Variant deleted successfully');
      fetchVariants();
    } catch (error) {
      console.error('Error deleting variant:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to delete variant';
      toast.error(errorMsg);
    }
  };

  const handleSyncVariant = async (variant) => {
    try {
      const response = await api.post(`/api/assessment-builder/variants/${variant.id}/sync-from-primary/`, {
        sync_questions: true,
        sync_screens: true,
        overwrite_existing: false
      });
      toast.success(`Synced from primary: ${response.data.summary?.questions_added || 0} questions added, ${response.data.summary?.screens_added || 0} screens added`);
      fetchVariants();
    } catch (error) {
      console.error('Error syncing variant:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to sync from primary';
      toast.error(errorMsg);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'variants', label: 'Variants', icon: Globe, count: variants?.length || 0 },
    { id: 'scoring', label: 'Scoring', icon: Calculator },
    { id: 'usage', label: 'Usage & Users', icon: Users },
    { id: 'versions', label: 'Version History', icon: Clock },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];
  
  // Fetch data when tabs are opened (lazy loading)
  useEffect(() => {
    if (activeTab === 'overview' && !overviewStats && !loadingOverview) {
      fetchOverviewStats();
    } else if (activeTab === 'usage' && !loadingUsage && usageData.studies.length === 0) {
      fetchUsageData();
    } else if (activeTab === 'versions' && versionHistory.length === 0 && !loadingVersions) {
      fetchVersionHistory();
    }
  }, [activeTab]);
  
  // The modal's assessment prop is the list payload, which has no items, so
  // fetch the detail record (same endpoint as the overview stats) on click.
  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessment.uuid}/`);
      const items = response.data?.items || [];
      if (items.length === 0) {
        toast.error('This assessment has no questions to export');
        return;
      }
      const name = response.data?.name || assessmentName;
      downloadCsv(buildAssessmentCsv(name, items), assessmentCsvFilename(name));
    } catch (error) {
      console.error('Error exporting assessment CSV:', error);
      toast.error('Failed to download CSV');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const fetchOverviewStats = async () => {
    setLoadingOverview(true);
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessment.uuid}/`);
      const data = response.data;
      setOverviewStats({
        question_count: data.question_count || 0,
        screen_count: data.screen_count || 0,
        variants: data.variants || []
      });
    } catch (error) {
      console.error('Error fetching overview stats:', error);
    } finally {
      setLoadingOverview(false);
    }
  };
  
  const fetchUsageData = async () => {
    setLoadingUsage(true);
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessment.uuid}/usage/`);
      setUsageData(response.data);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setLoadingUsage(false);
    }
  };
  
  const fetchVersionHistory = async () => {
    setLoadingVersions(true);
    try {
      // Use the survey-based endpoint (same as ScreenBuilder sidebar)
      const surveyId = assessment?.survey_id || assessment?.survey?.id;
      
      if (surveyId) {
        try {
          const response = await api.get(`/api/surveys/${surveyId}/versions/`);
          const data = response.data;
          // Handle both array response and {results: []} response
          const versions = Array.isArray(data) ? data : (data.results || data.versions || []);
          setVersionHistory(versions);
          return;
        } catch (surveyError) {
          console.warn('Survey versions endpoint failed, trying fallback:', surveyError.message);
        }
      }
      
      // Fallback to assessment-builder endpoint
      const response = await api.get(`/api/assessment-builder/assessments/${assessment.uuid}/version_history/`);
      const fallbackVersions = response.data.versions || [];
      // Normalize field names from fallback endpoint to match VersionsTab expectations
      const normalized = fallbackVersions.map(v => ({
        ...v,
        created_by_name: v.created_by_name || v.created_by__email || 'System',
      }));
      setVersionHistory(normalized);
    } catch (error) {
      console.error('Error fetching version history:', error);
    } finally {
      setLoadingVersions(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-gray-950/40 dark:bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-900 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Manage Assessment</h2>
            <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">View and edit assessment details</p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {canPublish && (
              <button
                onClick={() => setShowPublishModal(true)}
                className="btn btn-primary btn-sm"
              >
                <Globe className="h-3.5 w-3.5" aria-hidden="true" />
                Publish
              </button>
            )}
            <button
              onClick={onClose}
              className="btn btn-ghost btn-sm btn-icon -mr-2"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {showPublishModal && (
          <PublishAssessmentModal
            assessment={currentAssessment}
            onClose={() => setShowPublishModal(false)}
            onPublished={(result) => {
              setShowPublishModal(false);
              const updated = result?.publication_status
                ? result
                : { ...currentAssessment, publication_status: 'published' };
              handleAssessmentUpdate(updated);
              onRefresh && onRefresh();
            }}
          />
        )}

        {/* Assessment Name Section */}
        <div className="px-6 pt-5">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              {!editingName ? (
                <div className="flex items-center gap-2 group">
                  <h3 className="truncate text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">{assessmentName}</h3>
                  {currentAssessment?.publication_status !== 'published' ? (
                    <button
                      onClick={() => setEditingName(true)}
                      className="btn btn-ghost btn-sm btn-icon opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                      title="Edit name"
                    >
                      <Edit2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ) : (
                    <Lock className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" title="Published assessments cannot be renamed" />
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={assessmentName}
                    onChange={(e) => setAssessmentName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                    className="input max-w-md flex-1 text-base font-semibold"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={savingName}
                    className="btn btn-primary btn-sm"
                  >
                    {savingName ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingName(false);
                      setAssessmentName(assessment?.name || assessment?.survey?.name || '');
                    }}
                    className="btn btn-ghost btn-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <span className="code-chip">
                  v{assessment?.version || '1.0'}
                </span>
                <span className="badge badge-gray">{assessment?.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Library'}</span>
                <span className={`badge badge-dot ${STATUS_CHIP[currentStatus.color] || 'badge-gray'}`}>
                  {currentStatus.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-list mt-4 flex-shrink-0 px-6">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab ${isActive ? 'tab-active' : ''}`}
              >
                <TabIcon className="h-4 w-4" aria-hidden="true" />
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className="badge badge-gray tabular-nums">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <OverviewTab
                assessment={currentAssessment}
                overviewStats={overviewStats}
                loadingStats={loadingOverview}
                variants={variants}
                onEdit={onEdit}
                onView={onView}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                onDownloadCsv={handleDownloadCsv}
                downloadingCsv={downloadingCsv}
                editingDescription={editingDescription}
                setEditingDescription={setEditingDescription}
                assessmentDescription={assessmentDescription}
                setAssessmentDescription={setAssessmentDescription}
                handleSaveDescription={handleSaveDescription}
              />
            )}
            {activeTab === 'variants' && (
              <VariantsTab
                assessment={currentAssessment}
                variants={variants}
                loadingVariants={loadingVariants}
                onCreateVariant={(a) => { onCreateVariant(a); }}
                onVariantCreated={fetchVariants}
                onEdit={onEdit}
                getDeviceIcon={getDeviceIcon}
                onArchiveVariant={handleArchiveVariant}
                onDeleteVariant={handleDeleteVariant}
                onSyncVariant={handleSyncVariant}
              />
            )}
            {activeTab === 'usage' && (
              <UsageAndUsersTab
                assessment={currentAssessment}
                usageData={usageData}
                loading={loadingUsage}
                onRefresh={fetchUsageData}
                onAddUser={() => setAddUserModalOpen(true)}
              />
            )}
            {activeTab === 'versions' && (
              <VersionsTab 
                assessment={currentAssessment} 
                versionHistory={versionHistory}
                loading={loadingVersions}
              />
            )}
            {activeTab === 'scoring' && (
              <ScoringTab assessment={currentAssessment} />
            )}
            {activeTab === 'settings' && (
              <SettingsTab assessment={currentAssessment} onUpdate={handleAssessmentUpdate} />
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      
      {/* Add User Permission Modal */}
      <AddUserPermissionModal
        isOpen={addUserModalOpen}
        onClose={() => setAddUserModalOpen(false)}
        assessmentId={assessment?.uuid}
        onUserAdded={fetchUsageData}
      />
    </motion.div>
  );
};

// Overview Tab - Clean minimal design
const OverviewTab = ({
  assessment,
  overviewStats,
  loadingStats,
  variants = [],
  onEdit,
  onView,
  onDuplicate,
  onDelete,
  onDownloadCsv,
  downloadingCsv,
  editingDescription,
  setEditingDescription,
  assessmentDescription,
  setAssessmentDescription,
  handleSaveDescription
}) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.2, ease: 'easeOut' }}
    className="space-y-5"
  >
    {/* Stats Row */}
    <div className="grid gap-4 sm:grid-cols-3">
      {loadingStats ? (
        <>
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800" aria-hidden="true">
              <div className="h-3 w-20 rounded bg-gray-100 dark:bg-white/[0.06]"></div>
              <div className="mt-2 h-7 w-12 rounded bg-gray-100 dark:bg-white/[0.06]"></div>
            </div>
          ))}
        </>
      ) : (
        <>
          <OverviewStat label="Questions" value={overviewStats?.question_count || 0} icon={FileText} />
          <OverviewStat label="Screens" value={overviewStats?.screen_count || 0} icon={Layers} />
          <OverviewStat label="Variants" value={variants?.length || 0} icon={Globe} />
        </>
      )}
    </div>

    {/* Quick Actions */}
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={() => onEdit(assessment)}
        className="btn btn-primary"
      >
        <Edit2 className="h-4 w-4" aria-hidden="true" />
        Edit Assessment
      </button>
      {/* FECOA-1700 (AC-5): published assessments are locked, so Edit forces a
          new draft version. View opens the real canvas read-only instead. */}
      {onView && assessment?.publication_status === 'published' && (
        <button
          onClick={() => onView(assessment)}
          className="btn btn-secondary"
        >
          <Eye className="h-4 w-4" aria-hidden="true" />
          View
        </button>
      )}
      <button
        onClick={onDownloadCsv}
        disabled={downloadingCsv}
        className="btn btn-secondary disabled:cursor-wait"
      >
        <Download className="h-4 w-4" aria-hidden="true" />
        Download as CSV
      </button>
      <div className="flex-1" />
      {/* Only show archive button if user has permission (owner, admin, or superuser) */}
      {assessment?.can_delete && (
        assessment?.publication_status === 'published' ? (
          <div className="inline-flex h-9 cursor-not-allowed items-center gap-2 px-3 text-[13.5px] text-gray-400 dark:text-gray-500" title="Published assessments cannot be archived">
            <Lock className="h-4 w-4" aria-hidden="true" />
            <span>Locked</span>
          </div>
        ) : (
          <button
            onClick={() => onDelete(assessment)}
            className="btn btn-danger-soft"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Archive
          </button>
        )
      )}
    </div>

    {/* Two Column Layout */}
    <div className="grid items-start gap-5 lg:grid-cols-2">
      {/* Left Column - Details */}
      <div className="space-y-5">
        <div className="card p-5">
          <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Details</h3>
          <div className="mt-2 divide-y divide-gray-100 text-[13px] dark:divide-white/[0.06]">
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-gray-500 dark:text-gray-400">Type</span>
              <span className="text-right font-medium text-gray-900 dark:text-gray-50">{assessment?.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Library'}</span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-gray-500 dark:text-gray-400">Version</span>
              <span className="code-chip">v{assessment?.version || '1.0'}</span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-gray-500 dark:text-gray-400">Language</span>
              <span className="text-right font-medium text-gray-900 dark:text-gray-50">{assessment?.language || 'en-US'}</span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-gray-500 dark:text-gray-400">Therapeutic Area</span>
              <span className="text-right font-medium text-gray-900 dark:text-gray-50">{assessment?.therapeutic_area || 'General'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Dates & Description */}
      <div className="space-y-5">
        <div className="card p-5">
          <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Timeline</h3>
          <div className="mt-2 divide-y divide-gray-100 text-[13px] dark:divide-white/[0.06]">
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-gray-500 dark:text-gray-400">Created</span>
              <span className="text-right font-medium text-gray-900 dark:text-gray-50">{new Date(assessment?.created).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-gray-500 dark:text-gray-400">Last Modified</span>
              <span className="text-right font-medium text-gray-900 dark:text-gray-50">{new Date(assessment?.modified).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Description</h3>
            {!editingDescription && (
              <button
                onClick={() => setEditingDescription(true)}
                className="btn btn-ghost btn-sm -my-1 -mr-2"
              >
                Edit
              </button>
            )}
          </div>
          {!editingDescription ? (
            <p className="text-[13.5px] leading-relaxed text-gray-600 dark:text-gray-300">
              {assessmentDescription || 'No description provided. Click Edit to add one.'}
            </p>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <textarea
                  value={assessmentDescription}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 250) {
                      setAssessmentDescription(value);
                    }
                  }}
                  maxLength={250}
                  className="input min-h-[80px] resize-none pb-6"
                  placeholder="Enter assessment description (max 250 characters)..."
                  autoFocus
                />
                <div className="absolute bottom-2 right-2.5 text-xs tabular-nums text-gray-400 dark:text-gray-500">
                  {assessmentDescription.length}/250
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveDescription}
                  className="btn btn-primary btn-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingDescription(false);
                    setAssessmentDescription(assessment?.description || '');
                  }}
                  className="btn btn-ghost btn-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </motion.div>
);

// Variants Tab - Clean minimal design
const VariantsTab = ({ assessment, variants = [], loadingVariants, onCreateVariant, onVariantCreated, onEdit, getDeviceIcon, onArchiveVariant, onDeleteVariant, onSyncVariant }) => {
  const [openDropdown, setOpenDropdown] = React.useState(null);
  
  // Check if parent is draft (allows variant deletion)
  const parentIsDraft = assessment?.publication_status === 'draft';
  
  const handleDropdownToggle = (variantId) => {
    setOpenDropdown(openDropdown === variantId ? null : variantId);
  };
  
  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);
  
  return (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.2, ease: 'easeOut' }}
    className="space-y-5"
  >
    {/* Header */}
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-[13.5px] text-gray-500 dark:text-gray-400">
        Manage language and device-specific versions
      </p>
      <button
        onClick={() => onCreateVariant(assessment)}
        className="btn btn-primary"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Create Variant
      </button>
    </div>

    {/* Primary Assessment Card */}
    <div className="card p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3.5">
          <Avatar icon={Globe} size="md" tone="brand" />
          <div className="min-w-0">
            <div className="mb-0.5 flex items-center gap-2">
              <h4 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">{assessment?.language || 'en-US'}</h4>
              <span className="badge badge-brand">
                Primary
              </span>
            </div>
            <p className="text-[13px] text-gray-500 dark:text-gray-400">Web Desktop • Original assessment</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-[15px] font-semibold tabular-nums text-gray-900 dark:text-gray-50">{assessment?.question_count || 0}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Questions</div>
          </div>
          <div className="text-center">
            <div className="text-[15px] font-semibold tabular-nums text-gray-900 dark:text-gray-50">{assessment?.screen_count || 0}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Screens</div>
          </div>
          <button
            onClick={() => onEdit(assessment)}
            className="btn btn-secondary btn-sm"
          >
            <Edit2 className="h-3.5 w-3.5" aria-hidden="true" />
            Edit
          </button>
        </div>
      </div>
    </div>

    {/* Variant Cards */}
    {loadingVariants ? (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
      </div>
    ) : variants && variants.length > 0 ? (
      <div className="card divide-y divide-gray-100 dark:divide-white/[0.06]">
        <h4 className="px-5 py-4 text-[15px] font-semibold text-gray-900 dark:text-gray-50">
          Variants ({variants.length})
        </h4>
        {variants.map((variant) => {
          const DeviceIcon = getDeviceIcon(variant.device_type);
          const variantStatusConfig = {
            draft: { color: 'badge-warning', label: 'Draft' },
            published: { color: 'badge-success', label: 'Published' },
            archived: { color: 'badge-gray', label: 'Archived' }
          };
          const variantStatus = variantStatusConfig[variant.publication_status] || variantStatusConfig.draft;
          return (
            <div
              key={variant.id}
              className="px-5 py-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3.5">
                  <Avatar icon={DeviceIcon} size="sm" />
                  <div className="min-w-0">
                    <div className="mb-0.5 flex flex-wrap items-center gap-2">
                      <h4 className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">{variant.variant_assessment_name || `${assessment?.name} - ${variant.language}`}</h4>
                      <span className={`badge badge-dot ${variantStatus.color}`}>
                        {variantStatus.label}
                      </span>
                    </div>
                    <p className="text-[13px] text-gray-500 dark:text-gray-400">{variant.language} • {variant.device_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Question and Screen counts */}
                  <div className="flex items-center gap-4 text-[13px] text-gray-500 dark:text-gray-400">
                    <div className="text-center">
                      <div className="font-semibold tabular-nums text-gray-900 dark:text-gray-50">{variant.question_count || 0}</div>
                      <div className="text-xs">Questions</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold tabular-nums text-gray-900 dark:text-gray-50">{variant.screen_count || 0}</div>
                      <div className="text-xs">Screens</div>
                    </div>
                  </div>
                  <button
                    onClick={() => onEdit({
                      id: variant.variant_assessment,
                      uuid: variant.variant_assessment_uuid,
                      name: variant.variant_assessment_name,
                      library_type: assessment.library_type,
                      assessment_type: assessment.assessment_type
                    })}
                    className="btn btn-ghost btn-sm"
                  >
                    <Edit2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Edit
                  </button>
                  
                  {/* Actions Dropdown */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDropdownToggle(variant.id);
                      }}
                      className="btn btn-ghost btn-sm btn-icon"
                    >
                      <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                    </button>
                    
                    {openDropdown === variant.id && (
                      <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-xl border border-gray-200 bg-white p-1.5 shadow-float dark:border-white/10 dark:bg-gray-800">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(null);
                            onSyncVariant && onSyncVariant(variant);
                          }}
                          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13.5px] text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.06]"
                        >
                          <Download className="h-4 w-4 text-gray-400" aria-hidden="true" />
                          Sync from Primary
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(null);
                            onArchiveVariant && onArchiveVariant(variant);
                          }}
                          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13.5px] text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.06]"
                        >
                          <Archive className="h-4 w-4 text-gray-400" aria-hidden="true" />
                          Archive Variant
                        </button>
                        {parentIsDraft && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(null);
                              onDeleteVariant && onDeleteVariant(variant);
                            }}
                            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13.5px] text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                            Delete Variant
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="card flex flex-col items-center px-6 py-12 text-center">
        <Layers className="mb-3 h-6 w-6 text-gray-400 dark:text-gray-500" aria-hidden="true" />
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50">No Variants Yet</h4>
        <p className="mb-5 mt-1 text-sm text-gray-500 dark:text-gray-400">
          Create variants for different languages or devices
        </p>
        <button
          onClick={() => onCreateVariant(assessment)}
          className="btn btn-secondary"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Create First Variant
        </button>
      </div>
    )}
  </motion.div>
  );
};

// The usage endpoint returns permission_level as a display string ("Full Admin")
// for grantees and 'creator' for the owner; accept raw values too.
export const getAccessKey = (level) => {
  if (level === 'creator' || level === 'full_admin' || level === 'Full Admin') return 'full_admin';
  if (level === 'view' || level === 'View Only') return 'view';
  return 'edit';
};

const ACCESS_SECTIONS = [
  {
    key: 'full_admin',
    label: 'Full Admin',
    description: 'Can edit, publish, delete, and manage user access',
    icon: Shield,
    iconClasses: 'bg-white text-gray-500 ring-1 ring-inset ring-gray-200 dark:bg-white/[0.06] dark:text-gray-400 dark:ring-white/10',
    emptyText: 'No users with full admin access',
  },
  {
    key: 'edit',
    label: 'Edit Access',
    description: 'Can edit questions, screens, and content but cannot publish or delete',
    icon: Edit2,
    iconClasses: 'bg-white text-gray-500 ring-1 ring-inset ring-gray-200 dark:bg-white/[0.06] dark:text-gray-400 dark:ring-white/10',
    emptyText: 'No users with edit access',
  },
  {
    key: 'view',
    label: 'View Only',
    description: 'Can view the assessment but cannot make changes',
    icon: Eye,
    iconClasses: 'bg-white text-gray-500 ring-1 ring-inset ring-gray-200 dark:bg-white/[0.06] dark:text-gray-400 dark:ring-white/10',
    emptyText: 'No users with view only access',
  },
];

// Usage and Users Tab - Clean minimal design
const UsageAndUsersTab = ({ assessment, usageData, loading, onRefresh, onAddUser }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.2, ease: 'easeOut' }}
    className="space-y-8"
  >
    {loading ? (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading usage data...</p>
      </div>
    ) : (
      <>
        {/* Studies Using This Assessment */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Studies Using This Assessment</h3>
            <span className="badge badge-gray tabular-nums">
              {usageData?.studies?.length || 0}
            </span>
          </div>

          {usageData?.studies && usageData.studies.length > 0 ? (
            <div className="space-y-2">
              {usageData.studies.map((study) => (
                <div key={study.id} className="flex items-center justify-between gap-4 rounded-xl border border-gray-200/80 bg-white p-4 dark:border-white/[0.07] dark:bg-gray-800">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-400/10 dark:text-primary-400">
                      <Briefcase className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">{study.name}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{study.sponsor_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{study.participant_count || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">participants</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card">
              <EmptyState icon={Briefcase} title="Not currently used in any studies" />
            </div>
          )}
        </div>

        {/* User Access — one section per permission level */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User Access</h3>
            <button
              onClick={onAddUser}
              className="btn btn-primary btn-sm"
            >
              <Plus className="h-3.5 w-3.5" />
              Add User
            </button>
          </div>

          <div className="space-y-4">
            {ACCESS_SECTIONS.map((section) => {
              const SectionIcon = section.icon;
              const sectionUsers = (usageData?.users || [])
                .filter((user) => getAccessKey(user.permission_level) === section.key)
                .sort((a, b) => Number(b.permission_level === 'creator') - Number(a.permission_level === 'creator'));

              return (
                <div key={section.key} className="rounded-xl border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-gray-800">
                  <div className="flex items-center gap-3">
                    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${section.iconClasses}`}>
                      <SectionIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{section.label}</h4>
                        <span className="badge badge-gray tabular-nums">
                          {sectionUsers.length}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{section.description}</p>
                    </div>
                  </div>

                  {sectionUsers.length > 0 ? (
                    <div className="mt-3 space-y-1">
                      {sectionUsers.map((user) => (
                        <div key={user.id} className="flex items-center justify-between gap-3 rounded-lg bg-white p-3 ring-1 ring-inset ring-gray-200/70 dark:bg-gray-900/60 dark:ring-white/[0.06]">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700 dark:bg-primary-400/15 dark:text-primary-300">
                              {user.first_name?.[0] || user.email?.[0]?.toUpperCase()}{user.last_name?.[0] || ''}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                                  {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email?.split('@')[0]}
                                </h4>
                                {user.permission_level === 'creator' && (
                                  <span className="badge badge-brand">
                                    Owner
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400">{user.role_display}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 pl-11 text-xs text-gray-400 dark:text-gray-500 italic">{section.emptyText}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Change History / Audit Log */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Recent Activity</h3>
            {usageData?.changeLog && usageData.changeLog.length > 0 && (
              <button
                onClick={onRefresh}
                className="link text-xs"
              >
                Refresh
              </button>
            )}
          </div>

          {usageData?.changeLog && usageData.changeLog.length > 0 ? (
            <div className="space-y-3">
              {usageData.changeLog.map((change, idx) => (
                <div key={idx} className="flex gap-3 border-b border-gray-100 pb-3 last:border-0 dark:border-white/[0.06]">
                  <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500"></div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{change.action}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{change.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-medium">{change.user_name}</span>
                          <span>•</span>
                          <span>{new Date(change.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                      {change.changes_count && (
                        <span className="badge badge-gray tabular-nums">
                          {change.changes_count} {change.changes_count === 1 ? 'change' : 'changes'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card">
              <EmptyState icon={Activity} title="No recent changes recorded" />
            </div>
          )}
        </div>
      </>
    )}
  </motion.div>
);

// Versions Tab - Clean minimal design
const VersionsTab = ({ assessment, versionHistory, loading }) => {
  const currentVersion = assessment?.version || '1.0';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="space-y-6"
    >
      {/* Review audit trail (submissions, denials, approvals) — the same record as
          the card's Audit History, surfaced here so a reviewing admin can see who
          submitted / denied / approved, with timestamps and notes, before deciding
          (FECOA-1741). */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Review History</h3>
        <div className="card overflow-hidden">
          <ReviewHistoryInline entityType="assessment" objectId={assessment?.id} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Version History</h3>
          {!loading && versionHistory.length > 0 && (
            <span className="badge badge-gray tabular-nums">
              {versionHistory.length}
            </span>
          )}
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          Current: <span className="font-semibold text-gray-900 dark:text-gray-100">v{currentVersion}</span>
        </span>
      </div>

      {/* Restoring has to go through the open editor: the restored screens are saved
          back to the assessment from there, and the canvas reloads so an autosave
          cannot write the old content over them (FECOA-2189). */}
      {!loading && versionHistory.length > 1 && (
        <p className="-mt-3 text-sm text-gray-500 dark:text-gray-400">
          To restore an earlier version, open the assessment in the editor and use Versions in the toolbar.
        </p>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse rounded-xl border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-gray-800">
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
            </div>
          ))}
        </div>
      ) : versionHistory && versionHistory.length > 0 ? (
        <div className="space-y-2">
          {versionHistory.map((version, idx) => (
            <div
              key={version.id}
              className={`rounded-xl border p-4 transition-colors ${
                idx === 0
                  ? 'border-primary-200 bg-primary-50/60 dark:border-primary-800/40 dark:bg-primary-400/10'
                  : 'border-gray-200/70 bg-gray-50 dark:border-white/[0.07] dark:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`code-chip ${
                      version.version_type === 'auto' ? '' :
                      version.version_type === 'manual' ? 'bg-primary-50 text-primary-700 dark:bg-primary-400/10 dark:text-primary-300' :
                      version.version_type === 'publish' ? 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400' :
                      ''
                    }`}>
                      v{version.version_number}
                    </span>
                    {version.is_locked && (
                      <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    )}
                    {idx === 0 && (
                      <span className="badge badge-brand">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    <span>{version.created_by_name || 'System'}</span>
                    <span>•</span>
                    <span>{version.created ? new Date(version.created).toLocaleDateString() : 'Unknown'}</span>
                    {(version.screen_count !== undefined || version.element_count !== undefined) && (
                      <>
                        <span>•</span>
                        <span>{version.screen_count || 0} screens, {version.element_count || 0} elements</span>
                      </>
                    )}
                  </div>
                  {version.change_summary && (
                    <p className={`mt-2 text-sm ${idx === 0 ? 'text-primary-800 dark:text-primary-200' : 'text-gray-600 dark:text-gray-300'}`}>
                      {version.change_summary}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <EmptyState
            icon={Clock}
            title="No Version History"
            description="Versions will appear here as you save changes"
          />
        </div>
      )}
    </motion.div>
  );
};

// Settings Tab
// Whether the assessment is in adaptive (CAT) mode. Reads the new
// builder_metadata.adaptive_mode flag and falls back to the legacy
// builder_metadata.adaptive_import.enabled flag for back-compat.
export const isAdaptiveModeEnabled = (assessment) => {
  const md = assessment?.builder_metadata || {};
  return md.adaptive_mode === true || md.adaptive_import?.enabled === true;
};

// Derive editable adaptive-config form state from the assessment payload.
// Always returns a populated object so the UI can render the section the
// moment Adaptive Mode is toggled on, even before any data has been seeded.
export const buildAdaptiveImportState = (assessment) => {
  const importMeta = assessment?.builder_metadata?.adaptive_import || {};
  let surveyCfg = {};
  try {
    surveyCfg = JSON.parse(assessment?.survey_detail?.adaptive_config || '{}');
  } catch {
    surveyCfg = {};
  }

  return {
    sem_tolerance: importMeta.sem_tolerance ?? surveyCfg.SEMTol ?? 0.3,
    min_len: importMeta.min_len ?? surveyCfg.minLen ?? 4,
    max_len: importMeta.max_len ?? surveyCfg.maxLen ?? (surveyCfg.Items?.length ?? null),
    start_eap: surveyCfg.startEAP ?? 0,
    start_sem: surveyCfg.startSEM ?? 0,
    algorithm: surveyCfg.algorithm ?? 3,
    qpts: surveyCfg.qpts ?? 49,
    qmax: surveyCfg.qmax ?? 6,
    n_cats: surveyCfg.NCats ?? null,
    min_sem_delta: surveyCfg.minSEMDelta ?? null,
    constraints: surveyCfg.Constraints ?? [],
  };
};

export const getAdaptiveItemsCount = (assessment) => {
  // IRT calibration lives on the questions themselves
  // (AssessmentQuestion.irt_parameters); Survey.adaptive_config is only a
  // derived cache of that pool — and new-version forks don't copy it. Count
  // the questions when the payload carries them (authoritative), and only
  // fall back to the cache / import metadata for lightweight payloads.
  const items = assessment?.items || assessment?.questions;
  if (Array.isArray(items) && items.length > 0) {
    return items.filter((it) => {
      const irt = it?.irt_parameters || {};
      return Boolean(
        (Array.isArray(irt.slopes) && irt.slopes.length > 0) ||
        irt.discrimination ||
        irt.slope ||
        (Array.isArray(irt.thresholds) && irt.thresholds.length > 0)
      );
    }).length;
  }
  try {
    const cfg = JSON.parse(assessment?.survey_detail?.adaptive_config || '{}');
    if (cfg.Items?.length) return cfg.Items.length;
  } catch {
    // malformed cache — fall through to import metadata
  }
  return assessment?.builder_metadata?.adaptive_import?.item_count || 0;
};

// Human labels for the publication lifecycle. `review` is a legacy alias for
// the renamed canonical `in_review` value.
const STATUS_LABELS = {
  draft: 'Draft',
  review: 'Under Review',
  in_review: 'Under Review',
  approved: 'Approved',
  denied: 'Changes Requested',
  published: 'Published',
  archived: 'Archived',
};

const SettingsTab = ({ assessment, onUpdate }) => {
  const { user } = useAuth();
  // Break-glass: only a superuser may revert a published assessment to draft.
  const isSuperuser = !!user?.is_superuser;
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [adaptiveAdvancedOpen, setAdaptiveAdvancedOpen] = useState(false);

  // Build initial settings object
  const buildInitialSettings = () => {
    const base = {
      publication_status: assessment?.publication_status || 'draft',
      is_public: assessment?.is_public || false,
      respondent_type: assessment?.survey_detail?.respondent_type || assessment?.respondent_type || 'participant',
      regulatory_submission: assessment?.regulatory_submission || false,
      cdisc_compliant: assessment?.cdisc_compliant || false,
      builder_metadata: {
        allow_branching_logic: assessment?.builder_metadata?.allow_branching_logic ?? true,
        allow_question_randomization: assessment?.builder_metadata?.allow_question_randomization ?? false,
        allow_back_navigation: assessment?.builder_metadata?.allow_back_navigation ?? true,
        allow_skip_questions: assessment?.builder_metadata?.allow_skip_questions ?? false,
        show_progress_bar: assessment?.builder_metadata?.show_progress_bar ?? true,
        require_completion: assessment?.builder_metadata?.require_completion ?? false,
        enforce_question_timers: assessment?.builder_metadata?.enforce_question_timers ?? false,
        validate_responses: assessment?.builder_metadata?.validate_responses ?? true,
        calculate_scores_realtime: assessment?.builder_metadata?.calculate_scores_realtime ?? false,
        show_scores_to_participant: assessment?.builder_metadata?.show_scores_to_participant ?? false,
        enable_irt_parameters: assessment?.builder_metadata?.enable_irt_parameters ?? false,
        adaptive_mode: isAdaptiveModeEnabled(assessment),
      },
      device_compatibility: {
        mobile_optimized: assessment?.device_compatibility?.mobile_optimized ?? true,
        tablet_optimized: assessment?.device_compatibility?.tablet_optimized ?? true,
        desktop_only: assessment?.device_compatibility?.desktop_only ?? false,
        offline_capable: assessment?.device_compatibility?.offline_capable ?? false,
      },
      adaptive_import: buildAdaptiveImportState(assessment),
    };
    return base;
  };
  
  // Initialize settings from assessment data
  const [settings, setSettings] = useState(buildInitialSettings);
  const [initialSettings, setInitialSettings] = useState(buildInitialSettings);
  
  // Reset settings when assessment updates (e.g., after save/publish)
  useEffect(() => {
    console.log('[SettingsTab] Assessment changed, resetting settings:', assessment?.publication_status);
    const newSettings = {
      publication_status: assessment?.publication_status || 'draft',
      is_public: assessment?.is_public || false,
      respondent_type: assessment?.survey_detail?.respondent_type || assessment?.respondent_type || 'participant',
      regulatory_submission: assessment?.regulatory_submission || false,
      cdisc_compliant: assessment?.cdisc_compliant || false,
      builder_metadata: {
        allow_branching_logic: assessment?.builder_metadata?.allow_branching_logic ?? true,
        allow_question_randomization: assessment?.builder_metadata?.allow_question_randomization ?? false,
        allow_back_navigation: assessment?.builder_metadata?.allow_back_navigation ?? true,
        allow_skip_questions: assessment?.builder_metadata?.allow_skip_questions ?? false,
        show_progress_bar: assessment?.builder_metadata?.show_progress_bar ?? true,
        require_completion: assessment?.builder_metadata?.require_completion ?? false,
        enforce_question_timers: assessment?.builder_metadata?.enforce_question_timers ?? false,
        validate_responses: assessment?.builder_metadata?.validate_responses ?? true,
        calculate_scores_realtime: assessment?.builder_metadata?.calculate_scores_realtime ?? false,
        show_scores_to_participant: assessment?.builder_metadata?.show_scores_to_participant ?? false,
        enable_irt_parameters: assessment?.builder_metadata?.enable_irt_parameters ?? false,
        adaptive_mode: isAdaptiveModeEnabled(assessment),
      },
      device_compatibility: {
        mobile_optimized: assessment?.device_compatibility?.mobile_optimized ?? true,
        tablet_optimized: assessment?.device_compatibility?.tablet_optimized ?? true,
        desktop_only: assessment?.device_compatibility?.desktop_only ?? false,
        offline_capable: assessment?.device_compatibility?.offline_capable ?? false,
      },
      adaptive_import: buildAdaptiveImportState(assessment),
    };
    setSettings(newSettings);
    setInitialSettings(newSettings);
    setSaveSuccess(false);
  }, [assessment]);
  
  // Check if settings have changed from initial values
  const hasChanges = JSON.stringify(settings) !== JSON.stringify(initialSettings);

  // Status is read-only whenever the review/approval gate applies (publishing is
  // owned by the review workflow) or the assessment is already published. It stays
  // an editable dropdown only when self-publish is allowed — i.e. enforce_approval
  // is off for this study/sponsor/environment (testing assessments). Default to
  // locked when the flag is missing so we never expose direct publish by accident.
  const enforceApproval = assessment?.enforce_approval ?? true;
  const isPublished = assessment?.publication_status === 'published';
  const statusLocked = enforceApproval || isPublished;

  const handleSettingChange = (category, key, value) => {
    // Published assessments are read-only — block edits at the source instead
    // of letting a toggle flip and then erroring at save time.
    if (isPublished) return;
    if (category) {
      setSettings(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [key]: value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [key]: value
      }));
    }
    setSaveSuccess(false);
  };

  // Check if we're trying to publish (changing from non-published to published)
  const isPublishing = settings.publication_status === 'published' && 
    (assessment?.publication_status !== 'published');

  const handleSave = async () => {
    // If trying to publish, show confirmation modal instead
    if (isPublishing) {
      setShowPublishModal(true);
      return;
    }
    
    // Regular save for non-publish changes
    await performSave(settings);
  };

  const performSave = async (settingsToSave) => {
    try {
      setSaving(true);
      // Strip adaptive_import from the payload when Adaptive Mode is off.
      // It would otherwise round-trip through the backend's adaptive
      // validator, which rejects writes while adaptive_mode is disabled.
      const payload = settingsToSave?.builder_metadata?.adaptive_mode
        ? settingsToSave
        : (() => {
            const { adaptive_import: _unused, ...rest } = settingsToSave || {};
            return rest;
          })();
      const response = await api.patch(`/api/assessment-builder/assessments/${assessment.uuid}/update_settings/`, payload);
      const data = response.data;
      setSaveSuccess(true);
      toast.success('Settings saved successfully');
      
      // Call parent update handler to refresh assessment data
      if (onUpdate) {
        onUpdate(data.assessment);
      }

      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      const backendError = error?.response?.data?.error;
      const fieldErrors = error?.response?.data?.fields;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const summary = Object.entries(fieldErrors)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join('; ');
        toast.error(`${backendError || 'Invalid settings'} — ${summary}`);
      } else if (backendError) {
        toast.error(backendError);
      } else {
        toast.error('Failed to save settings. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Break-glass unpublish: revert a published assessment to draft. Superuser-only
  // in the UI; the backend unpublish endpoint is approve-permission gated, which a
  // superuser satisfies. (FECOA-1733)
  const handleUnpublish = async () => {
    try {
      setUnpublishing(true);
      await unpublishAssessment(assessment.uuid);
      toast.success('Assessment unpublished. It is now a draft.');
      if (onUpdate) {
        onUpdate({ ...assessment, publication_status: 'draft', status: 'draft' });
      }
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.detail;
      toast.error(msg || 'Could not unpublish assessment.');
    } finally {
      setUnpublishing(false);
      setShowUnpublishConfirm(false);
    }
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      
      // Include change summary in the request
      const publishSettings = {
        ...settings,
        change_summary: changeSummary || 'Published assessment'
      };
      
      const assessmentUuid = assessment?.uuid;
      if (!assessmentUuid) {
        toast.error('Assessment ID is missing');
        return;
      }

      // Strip adaptive_import when Adaptive Mode is off (same as performSave).
      // The backend's adaptive validator rejects any write carrying this key
      // while adaptive_mode is disabled, which would otherwise 400 every publish
      // of a non-adaptive assessment (FECOA-1529).
      const payload = publishSettings?.builder_metadata?.adaptive_mode
        ? publishSettings
        : (() => {
            const { adaptive_import: _unused, ...rest } = publishSettings || {};
            return rest;
          })();

      const response = await api.patch(`/api/assessment-builder/assessments/${assessmentUuid}/update_settings/`, payload);
      const data = response.data;
      console.log('[SettingsTab] Publish response:', data);
      console.log('[SettingsTab] data.assessment:', data.assessment);
      console.log('[SettingsTab] data.assessment.publication_status:', data.assessment?.publication_status);
      
      toast.success('Assessment published successfully! This version is now locked.');
      setShowPublishModal(false);
      setChangeSummary('');
      setSaveSuccess(true);
      
      // Call parent update handler to refresh assessment data
      // Merge with current assessment to preserve all fields
      const updatedAssessment = {
        ...assessment,
        ...(data.assessment || data),
        id: assessment?.id  // Ensure id is always preserved
      };
      console.log('[SettingsTab] Calling onUpdate with:', updatedAssessment);
      console.log('[SettingsTab] publication_status in update:', updatedAssessment.publication_status);
      if (onUpdate) {
        onUpdate(updatedAssessment);
      }

      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error publishing assessment:', error);
      const backendError = error?.response?.data?.error;
      toast.error(backendError || 'Failed to publish assessment. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  // Every setting locks once the assessment is published — the toggle renders
  // as a read-only state indicator (disabled defaults to isPublished).
  const ToggleSwitch = ({ checked, onChange, label, description, disabled = isPublished }) => (
    <div className="flex items-center justify-between py-3 group">
      <div className="pr-4">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
          {label}
          {disabled && <Lock className="h-3 w-3 text-gray-400 dark:text-gray-500" />}
        </h4>
        {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) onChange(!checked);
        }}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 ${
          checked ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-200 dark:bg-white/15'
        } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="space-y-8"
    >
      {/* Published lock banner — all settings below render read-only */}
      {isPublished && (
        <div className="flex items-start gap-2.5 rounded-lg border border-warning-500/30 bg-warning-50 px-4 py-3 dark:bg-warning-500/10">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" />
          <p className="text-sm text-warning-800 dark:text-warning-400">
            This assessment is published, so its settings are locked and shown read-only.
            Create a new draft version to make changes.
          </p>
        </div>
      )}

      {/* Publication Settings */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Publication</h3>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 space-y-4">
          <div>
            <label className="label">Status</label>
            {statusLocked ? (
              <>
                <div className="flex h-10 w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 text-sm text-gray-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200">
                  <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                  {STATUS_LABELS[settings.publication_status] || 'Draft'}
                </div>
                <p className="hint">
                  {isPublished
                    ? 'Published assessments are locked and cannot change status unless an admin reopens or unpublishes them.'
                    : 'Status is managed through the review workflow and cannot be changed here.'}
                </p>
                {isPublished && isSuperuser && (
                  <button
                    type="button"
                    onClick={() => setShowUnpublishConfirm(true)}
                    disabled={unpublishing}
                    className="btn btn-danger-soft btn-sm mt-2 border border-danger-100 dark:border-danger-500/30"
                    title="Break-glass: revert this published assessment to draft (superuser only)"
                  >
                    <Undo2 className="h-3.5 w-3.5" />
                    <span>{unpublishing ? 'Unpublishing…' : 'Unpublish (revert to draft)'}</span>
                  </button>
                )}
                <ConfirmDialog
                  isOpen={showUnpublishConfirm}
                  onClose={() => setShowUnpublishConfirm(false)}
                  onConfirm={handleUnpublish}
                  title="Unpublish assessment"
                  message={'Unpublish this assessment and revert it to draft?\n\nThis is a superuser break-glass action and will be recorded in the version history.'}
                  confirmText="Unpublish"
                  cancelText="Cancel"
                  type="danger"
                  isLoading={unpublishing}
                />
              </>
            ) : (
              <select
                className="input"
                value={settings.publication_status}
                onChange={(e) => handleSettingChange(null, 'publication_status', e.target.value)}
              >
                {/* FECOA-1691: "Published" removed — publishing is owned by the
                    review/approval workflow and must not be settable directly here. */}
                <option value="draft">Draft</option>
                <option value="in_review">Under Review</option>
                <option value="archived">Archived</option>
              </select>
            )}
          </div>
          <ToggleSwitch
            checked={settings.is_public}
            onChange={(value) => handleSettingChange(null, 'is_public', value)}
            label="Public Access"
            description="Allow all users in this sponsor to view"
          />
          {/* FECOA-1527: COA Type — editable while draft, locked once published */}
          <div className="pt-3">
            <label className="label flex items-center gap-1.5">
              COA Type
              {assessment?.publication_status === 'published' && (
                <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              )}
            </label>
            <select
              className="input"
              value={settings.respondent_type}
              onChange={(e) => handleSettingChange(null, 'respondent_type', e.target.value)}
              disabled={assessment?.publication_status === 'published'}
            >
              <option value="participant">PRO</option>
              <option value="caregiver">ObsRO</option>
              <option value="clinician">ClinRO</option>
            </select>
            {assessment?.publication_status === 'published' && (
              <p className="hint">
                COA type is locked once an assessment is published.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Respondent Experience (FECOA-1527: relabeled from "Participant Experience") */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Respondent Experience</h3>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 space-y-1 divide-y divide-gray-200/50 dark:divide-white/[0.06]">
          <ToggleSwitch
            checked={settings.builder_metadata.allow_branching_logic}
            onChange={(value) => handleSettingChange('builder_metadata', 'allow_branching_logic', value)}
            label="Allow Branching Logic"
            description="Enable conditional question display based on previous answers (navigation rules required)"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.allow_question_randomization}
            onChange={(value) => handleSettingChange('builder_metadata', 'allow_question_randomization', value)}
            label="Allow Question Randomization"
            description="Randomize question order for participants (not yet enforced)"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.allow_back_navigation}
            onChange={(value) => handleSettingChange('builder_metadata', 'allow_back_navigation', value)}
            label="Allow Back Navigation"
            description="Let participants go back to previous questions"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.allow_skip_questions}
            onChange={(value) => handleSettingChange('builder_metadata', 'allow_skip_questions', value)}
            label="Allow Skip Questions"
            description="Participants can skip optional questions"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.show_progress_bar}
            onChange={(value) => handleSettingChange('builder_metadata', 'show_progress_bar', value)}
            label="Show Progress Bar"
            description="Display completion progress to participants"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.require_completion}
            onChange={(value) => handleSettingChange('builder_metadata', 'require_completion', value)}
            label="Require Completion"
            description="Participants must complete all questions in one session (not yet enforced)"
          />
        </div>
      </div>

      {/* Scoring & Validation */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Scoring & Validation</h3>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 space-y-1 divide-y divide-gray-200/50 dark:divide-white/[0.06]">
          <ToggleSwitch
            checked={settings.builder_metadata.validate_responses}
            onChange={(value) => handleSettingChange('builder_metadata', 'validate_responses', value)}
            label="Validate Responses"
            description="Enforce answer format and range validation"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.enforce_question_timers}
            onChange={(value) => handleSettingChange('builder_metadata', 'enforce_question_timers', value)}
            label="Enforce Question Timers"
            description="Require minimum/maximum time per question (not yet enforced)"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.calculate_scores_realtime}
            onChange={(value) => handleSettingChange('builder_metadata', 'calculate_scores_realtime', value)}
            label="Calculate Scores in Real-time"
            description="Compute assessment scores as questions are answered (not yet enforced)"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.show_scores_to_participant}
            onChange={(value) => handleSettingChange('builder_metadata', 'show_scores_to_participant', value)}
            label="Show Scores to Participant"
            description="Display scores to participant after completion (not yet enforced)"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.enable_irt_parameters}
            onChange={(value) => handleSettingChange('builder_metadata', 'enable_irt_parameters', value)}
            label="Enable IRT Parameters"
            description="Allow adding Item Response Theory parameters when creating questions (discrimination, difficulty thresholds)"
          />
          <ToggleSwitch
            checked={settings.builder_metadata.adaptive_mode}
            onChange={(value) => handleSettingChange('builder_metadata', 'adaptive_mode', value)}
            label="Adaptive Mode"
            description="Run this assessment as a Computerized Adaptive Test (CAT). Requires IRT parameters on questions."
          />
        </div>
      </div>

      {/* Adaptive CAT Configuration — gated on Adaptive Mode toggle */}
      {settings.builder_metadata.adaptive_mode && (() => {
        const isLocked = assessment?.publication_status === 'published';
        const itemsCount = getAdaptiveItemsCount(assessment);
        const adaptive = settings.adaptive_import;
        const setAdaptive = (key, value) => handleSettingChange('adaptive_import', key, value);

        const numInput = (key, { step = 'any', placeholder = '', allowNull = false } = {}) => (
          <input
            type="number"
            step={step}
            disabled={isLocked}
            placeholder={placeholder}
            value={adaptive[key] ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                setAdaptive(key, allowNull ? null : '');
                return;
              }
              const n = Number(raw);
              setAdaptive(key, Number.isNaN(n) ? raw : n);
            }}
            className="input disabled:opacity-60"
          />
        );

        const fieldLabel = (text, helper) => (
          <div className="mb-1">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-200 block">{text}</label>
            {helper && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{helper}</p>}
          </div>
        );

        return (
          <div>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-primary-500" />
              Adaptive CAT Configuration
            </h3>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 space-y-5">
              {isLocked && (
                <div className="flex items-start gap-2 rounded-lg border border-warning-500/30 bg-warning-50 px-3 py-2 dark:bg-warning-500/10">
                  <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" />
                  <p className="text-xs text-warning-800 dark:text-warning-400">
                    This assessment is published. Create a new draft version to change adaptive engine settings.
                  </p>
                </div>
              )}

              {/* Items summary (read-only) — or no-items warning */}
              {itemsCount === 0 ? (
                <div className="flex items-start gap-2 rounded-lg border border-warning-500/30 bg-warning-50 px-3 py-2 dark:bg-warning-500/10">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" />
                  <p className="text-xs text-warning-800 dark:text-warning-400">
                    No IRT parameters on questions yet. Add IRT data to at least one question before saving these settings — saves will be rejected until then.
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600 dark:border-white/10 dark:bg-gray-900/40 dark:text-gray-300">
                  <span className="font-medium">{itemsCount}</span> item{itemsCount === 1 ? '' : 's'} in the Adaptest pool.
                  <span className="text-gray-500 dark:text-gray-400"> Max Items cannot exceed this count.</span>
                </div>
              )}

              {/* Core stopping rule */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">Stopping Rule</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    {fieldLabel('SEM Tolerance', 'Stop when SEM ≤ this value. Lower = more items.')}
                    {numInput('sem_tolerance', { step: '0.01' })}
                  </div>
                  <div>
                    {fieldLabel('Min Items', 'Engine will not stop until this many items have been served.')}
                    {numInput('min_len', { step: '1' })}
                  </div>
                  <div>
                    {fieldLabel('Max Items', itemsCount > 0 ? `Hard cap (≤ ${itemsCount}).` : 'Hard cap (cannot exceed the item pool).')}
                    {numInput('max_len', { step: '1' })}
                  </div>
                </div>
              </div>

              {/* Priors */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">Priors</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    {fieldLabel('Start EAP', 'Prior mean for theta. 0 = population average.')}
                    {numInput('start_eap', { step: '0.01' })}
                  </div>
                  <div>
                    {fieldLabel('Start SEM', 'Prior SD for theta. 1 = standard N(0,1) uninformative prior.')}
                    {numInput('start_sem', { step: '0.01' })}
                  </div>
                </div>
              </div>

              {/* Advanced disclosure */}
              <div>
                <button
                  type="button"
                  onClick={() => setAdaptiveAdvancedOpen((o) => !o)}
                  className="flex items-center gap-1 text-xs font-medium text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  {adaptiveAdvancedOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  Advanced
                </button>
                {adaptiveAdvancedOpen && (
                  <div className="mt-3 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        {fieldLabel('Algorithm', 'Adaptest algorithm selector. Currently only 3 is supported.')}
                        {numInput('algorithm', { step: '1' })}
                      </div>
                      <div>
                        {fieldLabel('qpts', 'Quadrature points used to approximate the posterior.')}
                        {numInput('qpts', { step: '1' })}
                      </div>
                      <div>
                        {fieldLabel('qmax', 'Theta range half-width: posterior spans [-qmax, +qmax].')}
                        {numInput('qmax', { step: '1' })}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        {fieldLabel('NCats (optional)', 'Response categories per item (e.g. 5 for PROMIS Likert). Leave blank to omit.')}
                        {numInput('n_cats', { step: '1', allowNull: true })}
                      </div>
                      <div>
                        {fieldLabel('Min SEM Delta (optional)', 'Stop early if SEM did not drop by at least this much since the last item.')}
                        {numInput('min_sem_delta', { step: '0.01', allowNull: true })}
                      </div>
                    </div>
                    <div>
                      {fieldLabel('Constraints', 'JSON array of [i, j] pairs preventing co-administration. Default: [].')}
                      <textarea
                        rows={3}
                        disabled={isLocked}
                        value={
                          typeof adaptive.constraints === 'string'
                            ? adaptive.constraints
                            : JSON.stringify(adaptive.constraints ?? [])
                        }
                        onChange={(e) => {
                          try {
                            setAdaptive('constraints', JSON.parse(e.target.value || '[]'));
                          } catch {
                            // Hold the raw string until the user fixes it; the backend will reject malformed payloads on save.
                            setAdaptive('constraints', e.target.value);
                          }
                        }}
                        className="input font-mono text-xs disabled:opacity-60"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Device Compatibility */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Device Compatibility</h3>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 space-y-1 divide-y divide-gray-200/50 dark:divide-white/[0.06]">
          <ToggleSwitch
            checked={settings.device_compatibility.mobile_optimized}
            onChange={(value) => handleSettingChange('device_compatibility', 'mobile_optimized', value)}
            label="Mobile Optimized"
            description="Optimize layout for mobile devices (metadata flag only)"
          />
          <ToggleSwitch
            checked={settings.device_compatibility.tablet_optimized}
            onChange={(value) => handleSettingChange('device_compatibility', 'tablet_optimized', value)}
            label="Tablet Optimized"
            description="Optimize layout for tablet devices (metadata flag only)"
          />
          <ToggleSwitch
            checked={settings.device_compatibility.desktop_only}
            onChange={(value) => handleSettingChange('device_compatibility', 'desktop_only', value)}
            label="Desktop Only"
            description="Restrict assessment to desktop browsers only (not yet enforced)"
          />
          <ToggleSwitch
            checked={settings.device_compatibility.offline_capable}
            onChange={(value) => handleSettingChange('device_compatibility', 'offline_capable', value)}
            label="Offline Capable"
            description="Allow participants to complete without internet (not yet supported)"
          />
        </div>
      </div>

      {/* Compliance */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Compliance</h3>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 space-y-1 divide-y divide-gray-200/50 dark:divide-white/[0.06]">
          <ToggleSwitch
            checked={settings.cdisc_compliant}
            onChange={(value) => handleSettingChange(null, 'cdisc_compliant', value)}
            label="CDISC Compliant"
            description="Assessment follows CDISC standards (metadata flag for exports)"
          />
          <ToggleSwitch
            checked={settings.regulatory_submission}
            onChange={(value) => handleSettingChange(null, 'regulatory_submission', value)}
            label="Regulatory Submission"
            description="Intended for regulatory submission (metadata flag only)"
          />
        </div>
      </div>

      {/* Save Button - Fixed at bottom, only show when changes detected.
          Never rendered for published assessments — everything is read-only. */}
      {!isPublished && (hasChanges || saveSuccess) && (
        <div className="sticky bottom-0 -mx-6 -mb-6 border-t border-gray-200 bg-white px-6 pb-4 pt-4 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.12)] dark:border-white/[0.06] dark:bg-gray-900 dark:shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.5)]">
          <div className="flex items-center justify-end gap-3">
            {saveSuccess && (
              <div className="mr-auto flex items-center gap-2 text-success-600 dark:text-success-400">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Saved</span>
              </div>
            )}
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/40 dark:bg-black/70">
          <div className="surface-overlay mx-4 w-full max-w-lg p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-lg bg-primary-50 p-2 text-primary-600 dark:bg-primary-400/10 dark:text-primary-400">
                <Lock className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Publish Assessment</h2>
            </div>
            
            <div className="mb-4 rounded-lg border border-warning-500/30 bg-warning-50 p-4 dark:bg-warning-500/10">
              <p className="text-sm text-warning-800 dark:text-warning-400">
                <strong>Important:</strong> Publishing will create a locked version <strong>(v{assessment?.version || '1.0'})</strong> that cannot be edited. 
                To make changes after publishing, you'll need to create a new version.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="label">
                Change Summary <span className="text-gray-400 dark:text-gray-500 text-xs font-normal">(optional)</span>
              </label>
              <textarea
                value={changeSummary}
                onChange={(e) => setChangeSummary(e.target.value)}
                placeholder="Describe the changes in this version..."
                className="input"
                rows="3"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPublishModal(false);
                  setChangeSummary('');
                  // Reset status back to original if user cancels
                  setSettings(prev => ({
                    ...prev,
                    publication_status: assessment?.publication_status || 'draft'
                  }));
                }}
                disabled={publishing}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="btn btn-primary flex-1"
              >
                {publishing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Publish
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ManageAssessment;
